import * as THREE from 'three';
import { Arena } from './Arena.js';
import { Base } from './Base.js';
import { seaTime } from './style.js';
import { PLAYER_COLORS, BASE_POSITIONS } from '../../../utils/constants.js';

// A bright tropical afternoon. The sun sits high in the south-west so the
// default view is cross-lit and the water glitters; it drifts very slowly
// round the sky through a match.
const SUN_ELEVATION = THREE.MathUtils.degToRad(52);
const SUN_AZIMUTH = THREE.MathUtils.degToRad(215);
const SUN_DRIFT = 0.004; // radians per second

const SHADOW_EXTENT = 85;
const SHADOW_MAP_SIZE = 2048;

export class Scene {
  constructor() {
    this.scene = new THREE.Scene();
    this.bases = [];
    this.matchClock = null;
    this.ambient = { sunDirection: new THREE.Vector3() };
    this.setupLighting();
    this.createArena();
    this.setSun(0);
  }

  setupLighting() {
    this.scene.fog = new THREE.Fog(0xc4e6ee, 260, 1300);

    // Sky-blue fill from above, warm sand bounce from below
    this.hemiLight = new THREE.HemisphereLight(0xcfe9ff, 0xc9b98f, 1.25);
    this.scene.add(this.hemiLight);

    // The sun, whose shadow box follows the action
    this.sun = new THREE.DirectionalLight(0xfff0d6, 3.1);
    this.sun.castShadow = true;
    const shadowCamera = this.sun.shadow.camera;
    shadowCamera.left = -SHADOW_EXTENT;
    shadowCamera.right = SHADOW_EXTENT;
    shadowCamera.top = SHADOW_EXTENT;
    shadowCamera.bottom = -SHADOW_EXTENT;
    shadowCamera.near = 1;
    shadowCamera.far = 500;
    this.sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.04;
    // Soften shadow edges a little
    this.sun.shadow.radius = 2;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);
  }

  createArena() {
    this.arena = new Arena(this.scene);
  }

  createBases(players) {
    // Clear existing bases
    this.bases.forEach(base => base.remove());
    this.bases = [];

    // Create bases for each player
    players.forEach((player, index) => {
      if (player) {
        const position = BASE_POSITIONS[index];
        const color = PLAYER_COLORS[index];
        const base = new Base(this.scene, position, color);
        this.bases.push(base);
      }
    });
  }

  startMatchClock() {
    this.matchClock = 0;
  }

  stopMatchClock() {
    this.matchClock = null;
  }

  setSun(elapsed) {
    const azimuth = SUN_AZIMUTH + elapsed * SUN_DRIFT;
    this.ambient.sunDirection.set(
      Math.cos(SUN_ELEVATION) * Math.sin(azimuth),
      Math.sin(SUN_ELEVATION),
      Math.cos(SUN_ELEVATION) * Math.cos(azimuth)
    ).normalize();
  }

  // focus: the point the camera is looking at
  update(deltaTime, elapsed, focus, renderer) {
    if (this.matchClock !== null) this.matchClock += deltaTime;
    seaTime.value = elapsed;
    this.setSun(elapsed);

    // Keep the shadow box centred on the action, snapped to whole shadow texels
    // so shadow edges don't shimmer as the camera moves
    const texel = (SHADOW_EXTENT * 2) / SHADOW_MAP_SIZE;
    const cx = Math.round(focus.x / texel) * texel;
    const cz = Math.round(focus.z / texel) * texel;
    this.sun.target.position.set(cx, 0, cz);
    this.sun.position.set(cx, 0, cz).addScaledVector(this.ambient.sunDirection, 250);

    this.arena.update(deltaTime, elapsed, this.ambient, focus);
    for (const base of this.bases) base.update(elapsed);
  }

  getOccluders() {
    return this.arena.getOccluders();
  }

  getScene() {
    return this.scene;
  }
}
